import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { DateRangePickerMobileComponent } from './date-range-picker-mobile/date-range-picker-mobile.component';
import { DateRangePickerDesktopComponent } from './date-range-picker-desktop/date-range-picker-desktop.component';
import { DateRangePickerService } from './date-range-picker.service';
import { Observable, combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [
    CommonModule,
    DateRangePickerMobileComponent,
    DateRangePickerDesktopComponent,
  ],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerComponent implements OnInit {
  // Desktop
  @Input() desktopFirstDatePlaceholder = '';
  @Input() desktopSecondDatePlaceholder = '';
  @Input() noSkipText = '';

  // Mobile
  @Input() mobileDatePlaceholder = '';
  @Input() mobileHeaderTitle = '';
  @Input() mobileConfirmButtonText = '';
  @Input() mobileSkipButtonText = '';

  // General
  @Input() withSkip = false;
  @Input() skipText = '';
  @Input() disabledFirstDateRange: Observable<DateTime[]>;
  @Input() disabledSecondDateRange: Observable<DateTime[]>;
  @Input() startDate: Observable<DateTime | undefined>;
  @Input() endDate: Observable<DateTime | undefined>;
  @Input() isSingleDate: Observable<boolean>;

  @Output() confirmClick = new EventEmitter<void>();
  @Output() skipClick = new EventEmitter<void>();
  @Output() dateChanges = new EventEmitter<{
    firstDate?: DateTime;
    secondDate?: DateTime;
    isSkipped: boolean;
  }>();

  private dateRangePickerService = inject(DateRangePickerService);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.subscribeDatesChange();
    this.updateDisabledDates();
    this.updateStartAndLastDates();
    this.updateSingleDate();
  }

  private subscribeDatesChange() {
    combineLatest([
      this.dateRangePickerService.firstDateFromRange$,
      this.dateRangePickerService.lastDateFromRange$,
      this.dateRangePickerService.isSkipped$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([firstDate, secondDate, isSkipped]) => {
        this.dateChanges.emit({ firstDate, secondDate, isSkipped });
      });
  }

  private updateDisabledDates() {
    this.disabledFirstDateRange
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((disabledFirstDateRange) => {
        this.dateRangePickerService.disabledFirstDateRange$.next(disabledFirstDateRange || []);
      });
      
    this.disabledSecondDateRange
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((disabledSecondDateRange) => {
        this.dateRangePickerService.disabledSecondDateRange$.next(disabledSecondDateRange || []);
      });
  }

  private updateStartAndLastDates() {
    this.startDate
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((startDate) => {
        if (!startDate) {
          return;
        }
        this.dateRangePickerService.startDate$.next(startDate);
      });

    this.endDate
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((endDate) => {
        if (!endDate) {
          return;
        }
        this.dateRangePickerService.endDate$.next(endDate);
      });
  }

  private updateSingleDate() {
    this.isSingleDate
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isSingleDate) => {
        this.dateRangePickerService.isSingleDate$.next(isSingleDate);

        // Reset last date if the date range picker is single date
        this.dateRangePickerService.lastDateFromRange$.next(undefined);
      });
  }
}
